import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { hasStoredKey, unlockWithPassword } from "@/protected/crypto";

/* The password is never in the code. It is turned into a decryption key in
   the browser (see src/protected/crypto.ts). A visitor is "authorized" when
   that key has successfully decrypted the check token in this tab. */

interface PortfolioAuthContextValue {
  isAuthorized: boolean;
  showModal: boolean;
  openModal: () => void;
  closeModal: () => void;
  authorize: (password: string) => Promise<boolean>;
}

const PortfolioAuthContext = createContext<PortfolioAuthContextValue | null>(null);

export function PortfolioAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthorized, setIsAuthorized] = useState(hasStoredKey);
  const [showModal, setShowModal] = useState(false);

  // The password is only asked when someone opens a protected case study
  // (or clicks a locked card). Public pages load straight away.

  const authorize = useCallback(async (password: string): Promise<boolean> => {
    const ok = await unlockWithPassword(password);
    if (ok) {
      setIsAuthorized(true);
      setShowModal(false);
    }
    return ok;
  }, []);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <PortfolioAuthContext.Provider value={{ isAuthorized, showModal, openModal, closeModal, authorize }}>
      {children}
    </PortfolioAuthContext.Provider>
  );
}

export function usePortfolioAuth(): PortfolioAuthContextValue {
  const ctx = useContext(PortfolioAuthContext);
  if (!ctx) {
    throw new Error("usePortfolioAuth must be used within a PortfolioAuthProvider");
  }
  return ctx;
}
